import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Fingerprint, Eye, Scale, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicHeader from "@/components/PublicHeader";
import SiteFooter from "@/components/SiteFooter";
import { useSeo, ORG_LD, SITE } from "@/lib/seo";
import { api } from "@/lib/api";
import { TopEmployers, ExampleScenarios } from "@/components/LandingSections";

const LANDING_LD = [ORG_LD, { "@type": "WebSite", name: "JobPedestal", url: SITE }];

const PRINCIPLES = [
  [Fingerprint, "Every listing has provenance", "Source, employer board, freshness and a working destination link — no recycled or seeded listings."],
  [ShieldCheck, "Evidence-backed applications", "Confirmed, candidate-reported and unconfirmed are never blurred. We never invent recruiter replies."],
  [Eye, "Consent-first visibility", "Recruiters only see you when you opt in. Export or delete your data at any time."],
  [Scale, "Explainable fit scores", "Each score is broken down factor by factor. A fit score is not a probability of being hired."],
];
const AGENTS = ["Resume Optimizer", "Cover Letter Studio", "Match Analyst", "Interview Coach", "Salary Negotiator", "Career Strategist", "LinkedIn Optimizer", "Application Assistant"];
const EXAMPLE = [["Skills evidence", 32, 40], ["Title alignment", 16, 20], ["Seniority fit", 15, 15], ["Compensation fit", 10, 10], ["Location & work mode", 10, 10]];

function LiveInventory() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/status").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  if (!s) return null;
  return (
    <p className="mt-8 font-mono text-xs text-slate-500" data-testid="landing-live-inventory">
      LIVE · {s.active_jobs.toLocaleString()} openings verified open · {s.components.find((c) => c.name.startsWith("Job sources"))?.name}
    </p>
  );
}

const ExampleCard = () => (
  <div className="relative rounded-3xl border bg-white p-7 shadow-xl fade-up" style={{ animationDelay: ".15s" }} data-testid="landing-example-card">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Illustration · how a fit score is explained</p>
    <div className="mt-4 flex items-start justify-between">
      <div><p className="font-display text-xl font-bold">VP, Supply Chain</p><p className="text-sm text-slate-500">Example employer · Hybrid · $240k–$310k</p></div>
      <span className="whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-mono text-sm font-semibold text-emerald-700">83 fit</span>
    </div>
    <div className="mt-6 space-y-3">
      {EXAMPLE.map(([f, p, m]) => (
        <div key={f}><div className="flex justify-between text-xs"><span>{f}</span><span className="font-mono">{p}/{m}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(p / m) * 100}%` }} /></div></div>
      ))}
    </div>
    <p className="mt-5 border-t pt-3 text-[11px] text-slate-500">Fit reflects profile alignment — not a probability of being hired.</p>
  </div>
);

export default function Landing() {
  useSeo({ title: "Verified jobs, explainable fit scores and AI career assistants", path: "/", jsonLd: LANDING_LD,
    description: "JobPedestal helps managers to C-suite find verified employer-direct job openings, understand their fit factor by factor, and prepare applications with JobPedestal Intelligence." });
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:py-28">
          <div className="fade-up">
            <p className="eyebrow mb-5">JobPedestal · AI career platform</p>
            <h1 className="text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">Run your next career move<br />on <span className="text-primary">evidence</span>, not noise.</h1>
            <p className="mt-6 max-w-xl text-base text-slate-600 sm:text-lg">For managers to C-suite across every industry. Verified openings from employer career sites, JobPedestal Intelligence with eight specialist career assistants, and an apply assistant that never submits without your approval.</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full px-7" data-testid="hero-get-started-button"><Link to="/register">Start free <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-recruiter-button"><Link to="/register?role=recruiter">I'm hiring</Link></Button>
            </div>
            <LiveInventory />
          </div>
          <ExampleCard />
        </div>
      </section>
      <TopEmployers />
      <section className="relative bg-[hsl(var(--obsidian))] py-24 text-white grain">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <p className="eyebrow mb-3 !text-[hsl(var(--gold))]">Our operating principles</p>
          <h2 className="max-w-2xl text-3xl font-bold sm:text-4xl">Built to be more transparent than premium job boards.</h2>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2">
            {PRINCIPLES.map(([Icon, t, d]) => (
              <div key={t} className="bg-[hsl(var(--obsidian))] p-8"><Icon className="h-6 w-6 text-[hsl(var(--gold))]" /><h3 className="mt-5 text-xl font-semibold">{t}</h3><p className="mt-2 text-sm text-slate-400">{d}</p></div>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div><p className="eyebrow mb-3">JobPedestal Intelligence · eight specialist assistants</p><h2 className="text-3xl font-bold">A career team that works from your real record.</h2>
            <p className="mt-4 text-slate-600">The assistants never fabricate experience, salary data or recruiter responses. Gaps are marked for you to fill.</p></div>
          <div className="grid gap-3 sm:grid-cols-2">
            {AGENTS.map((a, i) => (<div key={a} className="lift flex items-center gap-3 rounded-xl border bg-white p-4"><Sparkles className="h-4 w-4 text-primary" /><span className="font-medium">{a}</span><span className="ml-auto font-mono text-xs text-slate-400">0{i + 1}</span></div>))}
          </div>
        </div>
      </section>
      <ExampleScenarios />
      <section className="border-t bg-white py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:flex-row sm:items-center sm:px-8">
          <div><h2 className="text-2xl font-bold">Plans from $0. Executive Elite at $49.99/month.</h2><p className="mt-2 text-slate-600">Recruiter plans from $245/month, or $4.94 per candidate unlock. Enterprise on request.</p></div>
          <Button asChild variant="outline" className="rounded-full" data-testid="landing-pricing-button"><Link to="/pricing">Compare plans</Link></Button>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
